import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import UserAvatar from '@aurora/shared-client/components/users/UserAvatar/UserAvatar';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useId } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { SwitchedUserQuery, SwitchedUserQueryVariables } from '../../../types/graphql-types';
import type { FloatingControlDropdownItems } from '../../common/FloatingControlBox/FloatingControlBox';
import FloatingControlBox from '../../common/FloatingControlBox/FloatingControlBox';
import useTranslation from '../../useTranslation';
import switchedUserQuery from './SwitchedUser.query.graphql';
import localStyles from './SwitchUserFloatingBox.module.pcss';

export const SWITCH_USER_FLOATING_BOX_ID = 'switchUser';

interface Props {
  /**
   * Callback to be used to finish the impersonation.
   */
  onSwitchBackToMe: () => void;

  /**
   * Callback to be used to impersonate another user.
   */
  onSwitchToAnotherUser: () => void;
}

/**
 * Switch user floating control box. It's displayed when an admin is impersonating another user.
 *
 * @author Andre Almeida
 */
const SwitchUserFloatingBox: React.FC<React.PropsWithChildren<Props>> = ({
  onSwitchBackToMe,
  onSwitchToAnotherUser
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    formatMessage,
    loading: textLoading,
    FormattedMessage
  } = useTranslation(EndUserComponent.SWITCH_USER_FLOATING_BOX);

  // We cannot directly use the `authUser` on the `AppContext` since this component
  // lives above `AppContextProvider`.
  const { data: switchedUserData, loading: switchedUserLoading } = useQueryWithTracing<
    SwitchedUserQuery,
    SwitchedUserQueryVariables
  >(module, switchedUserQuery);

  const ariaId = `switch-user-menu-${useId()}`;

  if (textLoading || switchedUserLoading || !switchedUserData?.self) {
    return null;
  }

  const { self: user } = switchedUserData;

  const switchUserMenuItems: Array<FloatingControlDropdownItems> = [
    {
      onClick: () => onSwitchToAnotherUser(),
      title: formatMessage('switchToAnotherMember'),
      id: 'another'
    },
    {
      onClick: () => onSwitchBackToMe(),
      title: formatMessage('switchBackToMe'),
      id: 'me'
    }
  ];

  return (
    <FloatingControlBox
      testId="SwitchUserFloatingBox"
      dropdownId={ariaId}
      dropdownToggleLabel={formatMessage('toggleButtonLabel')}
      dropdownMenuItems={switchUserMenuItems}
    >
      <UserAvatar user={user} size={IconSize.PX_40} className={cx('lia-pulse')} />
      <div className={cx('lia-description')}>
        <FormattedMessage
          id="description"
          values={{
            userLogin: user.login,
            strong: function renderChunks(chunks) {
              return <strong>{chunks}</strong>;
            }
          }}
        />
      </div>
    </FloatingControlBox>
  );
};

export default SwitchUserFloatingBox;
